// ✅ src/components/admin/AdminProductsScreen.js
// Admin view of payout requests (withdrawals) + related referral history
// ✅ FIX: Transaction order (ALL reads before ANY writes) — fixes Firestore error
// ✅ When Mark PAID -> wallet.pending -> wallet.totalWithdrawn
// ✅ When Reject -> wallet.pending -> wallet.balance
// ✅ When Back to REQUESTED from PAID -> wallet.totalWithdrawn -> wallet.pending
// ✅ When Back to REQUESTED from REJECTED -> wallet.balance -> wallet.pending
// ✅ Adds wallet tx: WITHDRAW_PAID / WITHDRAW_REJECTED / WITHDRAW_BACK_TO_REQUESTED

import React, { useEffect, useMemo, useState } from "react";
import { auth, db } from "../../lib/firebase";
import { onAuthStateChanged } from "firebase/auth";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  where,
  runTransaction,
  increment,
} from "firebase/firestore";

function safe(v) {
  return String(v ?? "").trim();
}
function upper(v) {
  return safe(v).toUpperCase();
}
function moneyGHS(n) {
  const num = Number(n ?? 0);
  if (!Number.isFinite(num)) return "GHS 0";
  return `GHS ${num.toLocaleString()}`;
}
function fmtDate(ts) {
  try {
    if (!ts) return "—";
    const d = ts?.toDate ? ts.toDate() : new Date(ts);
    return d.toLocaleString();
  } catch {
    return "—";
  }
}
function maskUid(uid) {
  const u = safe(uid);
  if (!u) return "—";
  return u.length <= 12 ? u : `${u.slice(0, 6)}…${u.slice(-4)}`;
}
function copyText(t) {
  const s = safe(t);
  if (!s) return;
  try {
    navigator.clipboard.writeText(s);
  } catch {}
}

export default function AdminProductsScreen({ goBack, role: roleProp }) {
  const TABS = ["REQUESTED", "PAID", "REJECTED", "ALL"];

  /** @type {[import("firebase/auth").User|null, Function]} */
  const [user, setUser] = useState(null);

  /** @type {[string, Function]} */
  const [role, setRole] = useState(upper(roleProp));

  /** @type {[string, Function]} */
  const [tab, setTab] = useState("REQUESTED");

  /** @type {[any[], Function]} */
  const [rows, setRows] = useState([]);

  /** @type {[boolean, Function]} */
  const [loading, setLoading] = useState(true);

  /** @type {[string, Function]} */
  const [err, setErr] = useState("");

  /** @type {[string, Function]} */
  const [msg, setMsg] = useState("");

  /** @type {[string, Function]} */
  const [search, setSearch] = useState("");

  // expanded details per withdrawal id
  const [expanded, setExpanded] = useState({}); // { [withdrawalId]: boolean }
  const [details, setDetails] = useState({}); // { [withdrawalId]: {loading, err, wallet, referrals[]} }

  // 1) auth
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => {
      setUser(u || null);
      setErr("");
      setMsg("");
      if (!u) {
        setRows([]);
        setLoading(false);
        setErr("Please login first.");
      }
    });
    return () => unsub();
  }, []);

  // 2) role auto-load (if prop not passed)
  useEffect(() => {
    if (!user) return;

    if (safe(roleProp)) {
      setRole(upper(roleProp));
      return;
    }

    const unsub = onSnapshot(
      doc(db, "stores", user.uid),
      (snap) => {
        if (!snap.exists()) return setRole("");
        const d = snap.data() || {};
        const isAdmin = d.isAdmin === true;
        const r = upper(d.role);
        setRole(isAdmin ? "ADMIN" : r);
      },
      () => setRole("")
    );

    return () => unsub();
  }, [user, roleProp]);

  const isAllowed = role === "ADMIN" || role === "WORKER";

  // 3) live withdrawals feed
  useEffect(() => {
    if (!user || !isAllowed) return;

    setLoading(true);
    setErr("");
    setMsg("");

    const ref = collection(db, "withdrawals");
    const wheres = [];
    if (tab !== "ALL") wheres.push(where("status", "==", tab));

    const qy = query(ref, ...wheres, orderBy("createdAt", "desc"), limit(250));

    const unsub = onSnapshot(
      qy,
      (snap) => {
        const list = snap.docs.map((d) => ({ id: d.id, ...(d.data() || {}) }));
        setRows(list);
        setLoading(false);
        setErr("");

        // auto-refresh expanded items
        list.forEach((w) => {
          if (expanded[w.id]) fetchDetails(w.id, safe(w.uid));
        });
      },
      (e) => {
        console.error("withdrawals onSnapshot error:", e);
        setLoading(false);
        setErr(e?.message || "Query failed (rules/index?)");
      }
    );

    return () => unsub();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, isAllowed, tab]);

  const filtered = useMemo(() => {
    const q = safe(search).toLowerCase();
    if (!q) return rows;

    return rows.filter((w) => {
      const uid = safe(w.uid).toLowerCase();
      const network = safe(w.momoNetwork).toLowerCase();
      const number = safe(w.momoNumber).toLowerCase();
      const name = safe(w.momoName).toLowerCase();
      const status = safe(w.status).toLowerCase();
      return (
        uid.includes(q) ||
        network.includes(q) ||
        number.includes(q) ||
        name.includes(q) ||
        status.includes(q)
      );
    });
  }, [rows, search]);

  const totals = useMemo(() => {
    let count = 0;
    let sum = 0;
    filtered.forEach((w) => {
      count += 1;
      sum += Number(w.amount ?? 0) || 0;
    });
    return { count, sum };
  }, [filtered]);

  async function fetchDetails(withdrawalId, targetUid) {
    if (!withdrawalId || !targetUid) return;

    setDetails((p) => ({
      ...p,
      [withdrawalId]: { ...(p[withdrawalId] || {}), loading: true, err: "" },
    }));

    try {
      // wallet (optional)
      let wallet = null;
      try {
        const wSnap = await getDoc(doc(db, "wallets", targetUid));
        if (wSnap.exists()) wallet = wSnap.data() || null;
      } catch {
        // ignore wallet read errors
      }

      // referrals for this referrerUid
      let referrals = [];
      try {
        const rQ = query(
          collection(db, "referrals"),
          where("referrerUid", "==", targetUid),
          orderBy("updatedAt", "desc"),
          limit(60)
        );
        const rSnap = await getDocs(rQ);
        referrals = rSnap.docs.map((d) => ({ id: d.id, ...(d.data() || {}) }));
      } catch {
        const rQ2 = query(collection(db, "referrals"), where("referrerUid", "==", targetUid), limit(60));
        const rSnap2 = await getDocs(rQ2);
        referrals = rSnap2.docs.map((d) => ({ id: d.id, ...(d.data() || {}) }));
      }

      setDetails((p) => ({
        ...p,
        [withdrawalId]: { loading: false, err: "", wallet, referrals },
      }));
    } catch (e) {
      setDetails((p) => ({
        ...p,
        [withdrawalId]: {
          loading: false,
          err: e?.message || "Failed to load details (rules/index?)",
          wallet: null,
          referrals: [],
        },
      }));
    }
  }

  function toggleExpand(withdrawalId, targetUid) {
    const next = !expanded[withdrawalId];
    setExpanded((p) => ({ ...p, [withdrawalId]: next }));
    if (next) fetchDetails(withdrawalId, targetUid);
  }

  // ✅ FIXED: transaction order + supports Back to REQUESTED properly
  async function setStatus(withdrawal, nextStatus) {
    setErr("");
    setMsg("");

    try {
      const note =
        nextStatus === "REJECTED"
          ? prompt("Reject note (optional):") || ""
          : prompt("Payment note (optional):") || "";

      const withdrawalId = safe(withdrawal?.id);
      const targetUid = safe(withdrawal?.uid);
      const adminUid = safe(user?.uid);

      if (!withdrawalId || !targetUid) throw new Error("Missing withdrawal/uid");

      const wRef = doc(db, "withdrawals", withdrawalId);
      const walletRef = doc(db, "wallets", targetUid);

      await runTransaction(db, async (tx) => {
        // ✅ READS FIRST
        const wSnap = await tx.get(wRef);
        if (!wSnap.exists()) throw new Error("Withdrawal not found");

        const w = wSnap.data() || {};
        const currentStatus = upper(w.status);
        const amt = Number(w.amount ?? 0);
        if (!Number.isFinite(amt) || amt <= 0) throw new Error("Invalid amount");

        const walletSnap = await tx.get(walletRef);
        const hasWallet = walletSnap.exists();
        const wdoc = hasWallet ? walletSnap.data() || {} : {};

        const pending = Number(wdoc.pending ?? 0);
        const balance = Number(wdoc.balance ?? 0);
        const totalWithdrawn = Number(wdoc.totalWithdrawn ?? 0);

        // ✅ NOW WRITES
        tx.update(wRef, {
          status: nextStatus,
          processedAt: nextStatus === "REQUESTED" ? null : serverTimestamp(),
          processedBy: nextStatus === "REQUESTED" ? null : adminUid || null,
          note: safe(note),
          updatedAt: serverTimestamp(),
        });

        if (!hasWallet) return;

        // REQUESTED -> PAID
        if (nextStatus === "PAID" && currentStatus === "REQUESTED") {
          if (!Number.isFinite(pending) || pending < amt) {
            throw new Error("Wallet pending is less than withdrawal amount");
          }

          tx.update(walletRef, {
            pending: increment(-amt),
            totalWithdrawn: increment(amt),
            updatedAt: serverTimestamp(),
          });

          const txRef = doc(collection(db, "wallets", targetUid, "transactions"));
          tx.set(txRef, {
            type: "WITHDRAW_PAID",
            direction: "DEBIT",
            amount: amt,
            currency: safe(w.currency) || "GHS",
            status: "PAID",
            refId: withdrawalId,
            createdAt: serverTimestamp(),
          });
        }

        // REQUESTED -> REJECTED
        if (nextStatus === "REJECTED" && currentStatus === "REQUESTED") {
          if (!Number.isFinite(pending) || pending < amt) {
            throw new Error("Wallet pending is less than withdrawal amount");
          }

          tx.update(walletRef, {
            pending: increment(-amt),
            balance: increment(amt),
            updatedAt: serverTimestamp(),
          });

          const txRef = doc(collection(db, "wallets", targetUid, "transactions"));
          tx.set(txRef, {
            type: "WITHDRAW_REJECTED",
            direction: "CREDIT",
            amount: amt,
            currency: safe(w.currency) || "GHS",
            status: "REJECTED",
            refId: withdrawalId,
            createdAt: serverTimestamp(),
          });
        }

        // PAID -> REQUESTED (undo paid)
        if (nextStatus === "REQUESTED" && currentStatus === "PAID") {
          if (!Number.isFinite(totalWithdrawn) || totalWithdrawn < amt) {
            // still allow status change, but wallet may be inconsistent
            // throw to avoid breaking numbers:
            throw new Error("totalWithdrawn is less than amount (cannot revert)");
          }

          tx.update(walletRef, {
            totalWithdrawn: increment(-amt),
            pending: increment(amt),
            updatedAt: serverTimestamp(),
          });

          const txRef = doc(collection(db, "wallets", targetUid, "transactions"));
          tx.set(txRef, {
            type: "WITHDRAW_BACK_TO_REQUESTED",
            direction: "CREDIT",
            amount: amt,
            currency: safe(w.currency) || "GHS",
            status: "REQUESTED",
            refId: withdrawalId,
            createdAt: serverTimestamp(),
          });
        }

        // REJECTED -> REQUESTED (undo rejected)
        if (nextStatus === "REQUESTED" && currentStatus === "REJECTED") {
          if (!Number.isFinite(balance) || balance < amt) {
            throw new Error("balance is less than amount (cannot revert)");
          }

          tx.update(walletRef, {
            balance: increment(-amt),
            pending: increment(amt),
            updatedAt: serverTimestamp(),
          });

          const txRef = doc(collection(db, "wallets", targetUid, "transactions"));
          tx.set(txRef, {
            type: "WITHDRAW_BACK_TO_REQUESTED",
            direction: "DEBIT",
            amount: amt,
            currency: safe(w.currency) || "GHS",
            status: "REQUESTED",
            refId: withdrawalId,
            createdAt: serverTimestamp(),
          });
        }
      });

      setMsg("✅ Updated");
      window.setTimeout(() => setMsg(""), 1200);
    } catch (e) {
      console.error(e);
      setErr(e?.message || "❌ Failed (rules/index?)");
    }
  }

  if (!user) {
    return (
      <div style={S.page}>
        <div style={S.top}>
          <button style={S.back} onClick={goBack} type="button">
            ←
          </button>
          <div>
            <div style={S.h1}>Admin Payments</div>
            <div style={S.sub}>Login required.</div>
          </div>
        </div>
        <div style={S.card}>Please login first.</div>
      </div>
    );
  }

  if (!isAllowed) {
    return (
      <div style={S.page}>
        <div style={S.top}>
          <button style={S.back} onClick={goBack} type="button">
            ←
          </button>
          <div>
            <div style={S.h1}>Admin Payments</div>
            <div style={S.sub}>Access denied</div>
          </div>
          <div style={{ marginLeft: "auto" }}>
            <span style={S.badge}>{role || "NO ROLE"}</span>
          </div>
        </div>
        <div style={S.card}>You need ADMIN or WORKER.</div>
      </div>
    );
  }

  return (
    <div style={S.page}>
      <div style={S.top}>
        <button style={S.back} onClick={goBack} type="button">
          ←
        </button>

        <div style={{ minWidth: 0 }}>
          <div style={S.h1}>Admin Payments</div>
          <div style={S.sub}>Payout requests (MoMo) + referral history. Mark PAID / REJECTED.</div>
          <div style={S.sub2}>
            Showing <b>{totals.count}</b> • Total: <b>{moneyGHS(totals.sum)}</b>
          </div>
        </div>

        <div style={{ marginLeft: "auto", display: "flex", gap: 10, alignItems: "center" }}>
          <span style={S.badge}>{role}</span>
        </div>
      </div>

      <div style={S.wrap}>
        <div style={S.toolbar}>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            {TABS.map((t) => (
              <button
                key={t}
                style={{ ...S.tab, ...(tab === t ? S.tabOn : null) }}
                onClick={() => setTab(t)}
                type="button"
              >
                {t}
              </button>
            ))}
          </div>

          <input
            style={S.search}
            placeholder="Search uid / momo number / name / network / status"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        {msg ? <div style={S.msg}>{msg}</div> : null}
        {err ? <div style={S.errBox}>{err}</div> : null}

        {loading ? (
          <div style={S.card}>Loading…</div>
        ) : filtered.length === 0 ? (
          <div style={S.card}>No withdrawal requests in this view.</div>
        ) : (
          <div style={S.list}>
            {filtered.map((w) => {
              const st = upper(w.status || "REQUESTED");
              const targetUid = safe(w.uid);
              const isOpen = Boolean(expanded[w.id]);
              const det = details[w.id] || { loading: false, err: "", wallet: null, referrals: [] };

              return (
                <div key={w.id} style={S.item}>
                  <div style={S.itemTop}>
                    <div style={{ minWidth: 0 }}>
                      <div style={S.titleRow}>
                        <button
                          type="button"
                          style={S.expandBtn}
                          onClick={() => toggleExpand(w.id, targetUid)}
                        >
                          {isOpen ? "▾" : "▸"}
                        </button>

                        <div style={S.title}>
                          {moneyGHS(w.amount)} <span style={S.dot}>•</span> {safe(w.method || "MOMO")}
                        </div>

                        <span
                          style={{
                            ...S.pill,
                            ...(st === "REQUESTED" ? S.pillWarn : null),
                            ...(st === "PAID" ? S.pillGood : null),
                            ...(st === "REJECTED" ? S.pillBad : null),
                          }}
                        >
                          {st}
                        </span>
                      </div>

                      <div style={S.meta}>
                        uid: <b>{maskUid(targetUid)}</b> • created: <b>{fmtDate(w.createdAt)}</b>
                        {w.processedAt ? (
                          <>
                            {" "}
                            • processed: <b>{fmtDate(w.processedAt)}</b>
                          </>
                        ) : null}
                      </div>

                      <div style={S.meta}>
                        MoMo: <b>{safe(w.momoNetwork) || "—"}</b> • <b>{safe(w.momoNumber) || "—"}</b> •{" "}
                        <b>{safe(w.momoName) || "—"}</b>
                      </div>

                      {safe(w.note) ? <div style={S.noteLine}>Note: {safe(w.note)}</div> : null}

                      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 10 }}>
                        <button
                          style={S.smallBtn}
                          type="button"
                          onClick={() => {
                            copyText(`${safe(w.momoNetwork)} ${safe(w.momoNumber)} ${safe(w.momoName)}`);
                            setMsg("Copied MoMo details ✅");
                            window.setTimeout(() => setMsg(""), 1200);
                          }}
                        >
                          Copy MoMo
                        </button>

                        <button
                          style={S.smallBtn}
                          type="button"
                          onClick={() => {
                            copyText(targetUid);
                            setMsg("Copied uid ✅");
                            window.setTimeout(() => setMsg(""), 1200);
                          }}
                        >
                          Copy uid
                        </button>
                      </div>
                    </div>

                    <div style={S.actions}>
                      {st === "REQUESTED" ? (
                        <>
                          <button style={S.smallBtnGood} onClick={() => setStatus(w, "PAID")} type="button">
                            Mark PAID
                          </button>
                          <button style={S.smallBtnBad} onClick={() => setStatus(w, "REJECTED")} type="button">
                            Reject
                          </button>
                        </>
                      ) : (
                        <button style={S.smallBtn} onClick={() => setStatus(w, "REQUESTED")} type="button">
                          Back to REQUESTED
                        </button>
                      )}
                    </div>
                  </div>

                  {isOpen ? (
                    <div style={S.detailsBox}>
                      <div style={S.detailsGrid}>
                        {/* Wallet snapshot */}
                        <div>
                          <div style={S.blockTitle}>Wallet snapshot</div>
                          {det.loading ? (
                            <div style={S.miniNote}>Loading…</div>
                          ) : det.wallet ? (
                            <div style={S.cardInner}>
                              <div style={S.smallMeta}>
                                balance: <b>{moneyGHS(det.wallet.balance)}</b>
                                <br />
                                pending: <b>{moneyGHS(det.wallet.pending)}</b>
                                <br />
                                totalEarned: <b>{moneyGHS(det.wallet.totalEarned)}</b>
                                <br />
                                totalWithdrawn: <b>{moneyGHS(det.wallet.totalWithdrawn)}</b>
                              </div>
                            </div>
                          ) : (
                            <div style={S.miniNote}>No wallet doc (or rules blocked).</div>
                          )}
                        </div>

                        {/* Referrals */}
                        <div>
                          <div style={S.blockTitle}>Referrals for this user</div>

                          {det.loading ? (
                            <div style={S.miniNote}>Loading referrals…</div>
                          ) : det.err ? (
                            <div style={S.errMini}>{det.err}</div>
                          ) : det.referrals.length === 0 ? (
                            <div style={S.miniNote}>No referrals found for referrerUid.</div>
                          ) : (
                            <div style={S.refList}>
                              {det.referrals.slice(0, 30).map((r) => {
                                const status = upper(r.status || "PENDING");
                                const name = safe(r.sellerStoreName) || safe(r.sellerName) || "Referred user";
                                const reward = Number(r.rewardAmount ?? 7) || 7;

                                return (
                                  <div key={r.id} style={S.refRow}>
                                    <div style={{ minWidth: 0 }}>
                                      <div style={S.refTitle}>
                                        {name} <span style={S.badgeMini}>{status}</span>
                                      </div>
                                      <div style={S.smallMeta2}>
                                        referredUid: <b>{maskUid(r.referredUid)}</b> • updated:{" "}
                                        <b>{fmtDate(r.updatedAt || r.createdAt)}</b>
                                      </div>
                                    </div>
                                    <div style={S.refAmt}>{moneyGHS(reward)}</div>
                                  </div>
                                );
                              })}
                            </div>
                          )}

                          <div style={{ marginTop: 10, ...S.smallMeta2 }}>
                            Withdrawal ID: <span style={S.code}>{w.id}</span>
                          </div>
                          <div style={{ marginTop: 6, ...S.smallMeta2 }}>
                            User UID: <span style={S.code}>{targetUid || "—"}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : null}
                </div>
              );
            })}
          </div>
        )}

        <div style={S.note}>
          Index hints (if Firestore asks):
          <div style={{ marginTop: 8, display: "grid", gap: 6 }}>
            <code>withdrawals: status + createdAt desc</code>
            <code>referrals: referrerUid + updatedAt desc</code>
          </div>
        </div>
      </div>
    </div>
  );
}

const S = {
  page: {
    minHeight: "100vh",
    padding: 14,
    background: "linear-gradient(180deg,#f8fafc 0%, #eef2ff 45%, #ffffff 100%)",
    fontFamily: "Inter, system-ui, -apple-system, Segoe UI, Roboto, Arial, sans-serif",
  },
  top: {
    maxWidth: 1100,
    margin: "0 auto 14px",
    display: "flex",
    gap: 12,
    alignItems: "center",
  },
  back: {
    width: 42,
    height: 42,
    borderRadius: 14,
    border: "1px solid rgba(15,23,42,0.10)",
    background: "white",
    cursor: "pointer",
    fontWeight: 900,
  },
  h1: { fontSize: 18, fontWeight: 1000, color: "#0f172a" },
  sub: { marginTop: 2, fontSize: 12, fontWeight: 850, color: "rgba(15,23,42,0.60)" },
  sub2: { marginTop: 4, fontSize: 12, fontWeight: 950, color: "rgba(15,23,42,0.70)" },
  badge: {
    display: "inline-block",
    padding: "6px 10px",
    borderRadius: 999,
    border: "1px solid rgba(15,23,42,0.10)",
    background: "rgba(255,255,255,0.9)",
    fontSize: 12,
    fontWeight: 950,
    color: "#0f172a",
  },
  wrap: { maxWidth: 1100, margin: "0 auto" },

  toolbar: {
    display: "flex",
    gap: 12,
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "wrap",
    marginBottom: 12,
    padding: 12,
    borderRadius: 18,
    border: "1px solid rgba(15,23,42,0.10)",
    background: "rgba(255,255,255,0.92)",
    boxShadow: "0 14px 35px rgba(15, 23, 42, 0.06)",
  },

  tab: {
    padding: "10px 12px",
    borderRadius: 999,
    border: "1px solid rgba(15,23,42,0.10)",
    background: "rgba(255,255,255,0.85)",
    cursor: "pointer",
    fontWeight: 1000,
    fontSize: 12,
  },
  tabOn: {
    background: "rgba(99,102,241,0.12)",
    border: "1px solid rgba(99,102,241,0.22)",
  },

  search: {
    flex: "1 1 320px",
    minWidth: 220,
    padding: "10px 12px",
    borderRadius: 14,
    border: "1px solid rgba(15,23,42,0.12)",
    outline: "none",
    fontWeight: 900,
    background: "white",
  },

  msg: {
    marginBottom: 12,
    padding: 10,
    borderRadius: 14,
    border: "1px solid rgba(15,23,42,0.08)",
    background: "rgba(255,255,255,0.75)",
    fontSize: 12,
    fontWeight: 950,
  },

  errBox: {
    marginBottom: 12,
    padding: 10,
    borderRadius: 14,
    border: "1px solid rgba(239,68,68,0.20)",
    background: "rgba(239,68,68,0.10)",
    fontSize: 12,
    fontWeight: 950,
    color: "#b91c1c",
  },

  card: {
    padding: 14,
    borderRadius: 18,
    border: "1px solid rgba(15,23,42,0.10)",
    background: "rgba(255,255,255,0.92)",
    boxShadow: "0 14px 35px rgba(15, 23, 42, 0.06)",
  },

  list: { display: "grid", gap: 12 },

  item: {
    borderRadius: 18,
    border: "1px solid rgba(15,23,42,0.10)",
    background: "rgba(255,255,255,0.92)",
    boxShadow: "0 14px 35px rgba(15, 23, 42, 0.06)",
    padding: 14,
  },

  itemTop: { display: "flex", gap: 12, alignItems: "flex-start", justifyContent: "space-between" },

  titleRow: { display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" },
  title: { fontSize: 14, fontWeight: 1000, color: "#0f172a" },
  dot: { opacity: 0.35 },

  expandBtn: {
    width: 34,
    height: 34,
    borderRadius: 12,
    border: "1px solid rgba(15,23,42,0.10)",
    background: "white",
    cursor: "pointer",
    fontWeight: 1000,
  },

  pill: {
    fontSize: 11,
    fontWeight: 1000,
    padding: "5px 10px",
    borderRadius: 999,
    border: "1px solid rgba(15,23,42,0.10)",
    background: "rgba(248,250,252,0.85)",
  },
  pillWarn: {
    border: "1px solid rgba(245,158,11,0.25)",
    background: "rgba(245,158,11,0.12)",
  },
  pillGood: {
    border: "1px solid rgba(34,197,94,0.25)",
    background: "rgba(34,197,94,0.12)",
  },
  pillBad: {
    border: "1px solid rgba(239,68,68,0.22)",
    background: "rgba(239,68,68,0.10)",
  },

  meta: { marginTop: 6, fontSize: 12, fontWeight: 850, color: "rgba(15,23,42,0.65)" },
  noteLine: { marginTop: 8, fontSize: 12, fontWeight: 850, color: "rgba(15,23,42,0.75)" },

  actions: { display: "grid", gap: 8, minWidth: 170 },

  smallBtn: {
    padding: "8px 10px",
    borderRadius: 14,
    border: "1px solid rgba(15,23,42,0.10)",
    background: "white",
    cursor: "pointer",
    fontWeight: 950,
    fontSize: 12,
  },
  smallBtnGood: {
    padding: "8px 10px",
    borderRadius: 14,
    border: "1px solid rgba(34,197,94,0.22)",
    background: "rgba(34,197,94,0.12)",
    cursor: "pointer",
    fontWeight: 1000,
    fontSize: 12,
  },
  smallBtnBad: {
    padding: "8px 10px",
    borderRadius: 14,
    border: "1px solid rgba(239,68,68,0.20)",
    background: "rgba(239,68,68,0.10)",
    cursor: "pointer",
    fontWeight: 1000,
    fontSize: 12,
  },

  detailsBox: {
    marginTop: 12,
    padding: 12,
    borderRadius: 16,
    border: "1px solid rgba(15,23,42,0.10)",
    background: "rgba(248,250,252,0.70)",
  },
  detailsGrid: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 },

  blockTitle: { fontWeight: 1100, fontSize: 13, marginBottom: 8, color: "#0f172a" },

  miniNote: {
    padding: 10,
    borderRadius: 14,
    border: "1px solid rgba(15,23,42,0.08)",
    background: "rgba(255,255,255,0.75)",
    fontSize: 12,
    fontWeight: 900,
    color: "rgba(15,23,42,0.70)",
  },

  errMini: {
    padding: 10,
    borderRadius: 14,
    border: "1px solid rgba(239,68,68,0.20)",
    background: "rgba(239,68,68,0.10)",
    fontSize: 12,
    fontWeight: 950,
    color: "#b91c1c",
  },

  cardInner: {
    padding: 12,
    borderRadius: 14,
    border: "1px solid rgba(15,23,42,0.08)",
    background: "rgba(255,255,255,0.82)",
  },

  refList: { display: "grid", gap: 10 },
  refRow: {
    display: "flex",
    justifyContent: "space-between",
    gap: 12,
    padding: 10,
    borderRadius: 14,
    border: "1px solid rgba(15,23,42,0.10)",
    background: "rgba(255,255,255,0.75)",
  },
  refTitle: { fontSize: 13, fontWeight: 1000, color: "#0f172a" },
  refAmt: { fontSize: 13, fontWeight: 1000, whiteSpace: "nowrap" },
  badgeMini: {
    display: "inline-block",
    marginLeft: 8,
    padding: "4px 8px",
    borderRadius: 999,
    border: "1px solid rgba(15,23,42,0.10)",
    background: "rgba(248,250,252,0.9)",
    fontSize: 11,
    fontWeight: 950,
  },

  smallMeta: { fontSize: 12, fontWeight: 900, color: "rgba(15,23,42,0.75)", lineHeight: 1.6 },
  smallMeta2: { fontSize: 12, fontWeight: 850, color: "rgba(15,23,42,0.68)", lineHeight: 1.55 },

  code: {
    fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
    fontSize: 12,
    padding: "2px 6px",
    borderRadius: 8,
    background: "rgba(15,23,42,0.06)",
    border: "1px solid rgba(15,23,42,0.08)",
  },

  note: {
    marginTop: 12,
    padding: 10,
    borderRadius: 14,
    border: "1px solid rgba(15,23,42,0.08)",
    background: "rgba(255,255,255,0.75)",
    fontSize: 12,
    fontWeight: 850,
    color: "rgba(15,23,42,0.72)",
  },
};
